import { useState, ChangeEvent, ReactNode, FC, useEffect, useRef } from 'react'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { styled } from '@mui/material/styles'
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone'

export const SearchInputWrapper = styled(TextField)(
  ({ theme }) => `
    background: ${theme.colors.alpha.white[100]};
    .MuiInputBase-input {
        font-size: ${theme.typography.pxToRem(17)};
    }
`
)

interface SearchProps {
  children?: ReactNode
  placeholder?: string
  onSearch: (searchValue: string) => void
  /** When provided, the input is controlled by this value (e.g. from Redux) */
  value?: string
}

export const Search: FC<SearchProps> = ({
  onSearch,
  placeholder = 'Search terms here...',
  value: controlledValue,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const isControlled = controlledValue !== undefined
  const displayValue = isControlled ? (controlledValue ?? '') : searchValue

  // Detect platform for keyboard shortcut hint
  const getKeyboardShortcut = (): string => {
    if (typeof window === 'undefined') return 'Ctrl+F'
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
    return isMac ? 'Cmd+F' : 'Ctrl+F'
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = event.target.value
    if (isControlled) {
      onSearch(newValue)
    } else {
      setSearchValue(newValue)
    }
  }

  useEffect(() => {
    if (!isControlled) {
      onSearch(searchValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+F (Windows/Linux) or Cmd+F (Mac)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const isShortcut = isMac 
        ? (event.metaKey && event.key === 'f')
        : (event.ctrlKey && event.key === 'f')

      if (isShortcut) {
        event.preventDefault()
        // Focus the search input
        if (inputRef.current) {
          const inputElement = inputRef.current.querySelector('input')
          if (inputElement) {
            inputElement.focus()
            inputElement.select()
          }
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const shortcut = getKeyboardShortcut()
  const placeholderWithShortcut = `Search (${shortcut})`

  return (
    <SearchInputWrapper
      ref={inputRef}
      value={displayValue}
      onChange={handleSearchChange}
      InputProps={{
        ...(displayValue && {
          endAdornment: (
            <InputAdornment position="end" sx={{ minHeight: 10 }}>
              <IconButton
                onClick={() => {
                  if (isControlled) {
                    onSearch('')
                  } else {
                    setSearchValue('')
                  }
                }}
              >
                <ClearTwoToneIcon />
              </IconButton>
            </InputAdornment>
          ),
        }),
      }}
      placeholder={placeholderWithShortcut}
      size="small"
      fullWidth
      label={placeholderWithShortcut}
    />
  )
}
